import { DEV_VERSION } from 'app/components/version';
import { env } from 'app/lib/env';
import { create, isAxiosError } from 'axios';
import Constants from 'expo-constants';
import qs from 'qs';
import { recordError } from '../monitoring';
import { contentType } from './interceptors/content-type';
import { logging } from './interceptors/logging';
import { responseInError } from './interceptors/response-in-error';
import { requestPerformance, responsePerformance, responsePerformanceError } from './interceptors/performance';
import { createSessionManagementInterceptor, UserLoggedOutError } from './interceptors/session-management';
import { WHITE_LABEL } from 'app/../app.config.json';
import { apiEndpointManager, installApiEndpointFailover } from './api-endpoint';
import config from 'app/lib/config';
import { hydrateSessionState } from './session-state';

// API version: bump when implementing a backward incompatible change that requires the backend to know if the app supports of not a specific response
// Version 2: support for $signals ; deployed in v1.9.8-9 (2023-12-06)
// Version 3: support for channel.custom_type = support ; deployed in v1.9.11-9 (2024-04-04)
// Version 4: mobile now supports NeedAppUpdate signal (before that, server will need to use the GET /listings API response to ask users to update); deployed in v1.9.12-2 (2024-04-24)
// Version 5: support for pending loans in response of GET /loans; deployed in v1.9.13-10 (2024-06-05)
// Version 6: support signal.data (defricheur|trial) when receiving the freeSubAvailable signal; deployed in v1.9.13-25 (2024-06-27)
// Version 7: changed how we confirm the completion of a loan (to support multiple accepted loans per listing); deployed in v1.9.15-7 (2024-08-27)
// Version 8: no more filtering on distance, when listing items; deployed in v1.9.15-60 (2024-12-16)
// Version 9: support for ListingSubType.service; deployed in v1.9.19-11 (2025-03-28)
// Version 10: re-added filtering on distance, when listing items; deployed in v1.9.19-35 (2025-05-30)
// Version 11: support for grouped-channels (type = listing_group | user_group); deployed in v1.12.0-7 (2026-04-20)
// Version 12: support for group chats; deployed in v1.12.2-10 (2026-06-02)
// Version 13: support for selective BFF branches; deployed in v1.12.5-4 (2026-07-22)
// Version 14: support for API_REGION_MISMATCH regional authentication routing
// Version 15: database-configured code prompts and explicit confirmationAccepted; deployed in v1.13.1
export const API_VERSION = '16';

export const http = create({
  headers: {
    'accept-version': __DEV__ ? DEV_VERSION : Constants.expoConfig!.version,
    'lpc-api-version': API_VERSION,
    'lpc-flavor': WHITE_LABEL ? WHITE_LABEL.toLowerCase() : env.IS_PARTAKE ? 'partake' : 'partageclub',
  },
  baseURL: env.API_BASE_URL,
  allowAbsoluteUrls: false,
  paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }) },
  transformRequest: (data) => (data instanceof FormData ? data : JSON.stringify(data)),
});

installApiEndpointFailover(http, apiEndpointManager, hydrateSessionState, {
  routeGuestListingsToCanada: () => config.force_canadian_listings_for_guests_enabled,
});

const sessionManagement = createSessionManagementInterceptor(http);
const recordedServerErrors = new WeakSet<Error>();
[contentType, logging, sessionManagement, requestPerformance].forEach((interceptor) => {
  http.interceptors.request.use(interceptor);
});

http.interceptors.response.use(responsePerformance, responsePerformanceError);

http.interceptors.response.use(
  (res) => res,
  (error: Error) => {
    // UserLoggedOutError is expected behaviour (session expired / no refresh token); do not report to Sentry.
    // Also restrict to 5xx server errors to avoid recording expected client-side rejections as empty objects
    // (JSON.stringify on an Error returns "{}", which Sentry records as <unknown>).
    if (
      !(error instanceof UserLoggedOutError) &&
      isAxiosError(error) &&
      (error.response?.status ?? 0) >= 500 &&
      !recordedServerErrors.has(error)
    ) {
      recordedServerErrors.add(error);
      recordError(error);
    }
    return Promise.reject(error);
  },
);

// Inject the API-provided error message (found in the response body), if any, into the error.message, for components to be able to display those
http.interceptors.response.use(
  (res) => res,
  (error) => responseInError(error),
);
